/* istanbul ignore file */

/*! Please do not import any other dependencies or files has dependencies !*/
import { SettingsIcon } from '@panwds/icons'
import { MSP_MICRO_APP, ASC_TIER_1_SUPPORT_ROLE } from './common/constants/GlobalConstants'
import type { NavParams, NavItem, MicroAppConfig, StoreState } from '@sparky/framework'
import type { CommonModuleState } from './common/types'
// Only lodash, @panwds/icons, svg, constants, and types can be imported here!
// Please do not import feature flag or sparky util here, use `sparkyFramework` and `vars` in microApp fn

// temp solution: remove when aiops-free become a separate shell
const AIOPS_FREE_PREFIX = '/aiops-free'
const notAiopsFree = () => !window.location.pathname.startsWith(AIOPS_FREE_PREFIX)
const commonServicesAvail = {
  avail: ({ access }: any) => notAiopsFree() && !access?.hasRoleOnly?.(ASC_TIER_1_SUPPORT_ROLE),
  availFallbackRedirect: () => (notAiopsFree() ? undefined : AIOPS_FREE_PREFIX),
}

const importComponent = (name: string, extra?: Record<string, string | boolean>) => {
  const fn = () => import('./components').then((module: any) => ({ default: module[name] }))
  fn.lazy = true
  if (extra) {
    Object.assign(fn, extra)
  }
  return fn
}

const Subscriptions = importComponent('Subscriptions')
const TenantManagement = importComponent('TenantManagement')
const DevicesAssociations = importComponent('DevicesAssociations')
const IAMTabs = importComponent('IAM', { layout: 'tabs' })
const IpAllowList = importComponent('IpAllowList')
const UserPreferences = importComponent('UserPreferences')
const SubscriptionFlowCheck = importComponent('SubscriptionFlowCheck')
const Activation = importComponent('Activation')
const Hub = importComponent('Hub')
const ZTPDeviceActivation = importComponent('ZTPDeviceActivation')
const TsgNavIcon = importComponent('TsgNavIcon')
const TenantAppSwitcher = importComponent('TenantAppSwitcher')
const TenantSwitcher = importComponent('TenantSwitcher')
const Dev = () => Promise.resolve({ default: () => null })

type SparkyStoreState = StoreState & { commonModule?: CommonModuleState }

export const modules = {
  Subscriptions,
  TenantManagement,
  DevicesAssociations,
  IAMTabs,
  IpAllowList,
  UserPreferences,
  SubscriptionFlowCheck,
  Activation,
  Hub,
  ZTPDeviceActivation,
  TsgNavIcon,
  TenantAppSwitcher,
  TenantSwitcher,
  Dev,
}

const microApp = ({ vars, sparkyFramework }: NavParams): MicroAppConfig | null => {
  if (!sparkyFramework.hasTsgSupport()) {
    return null
  }

  sparkyFramework.actions.loadApps().catch(() => { })

  const fxVars = sparkyFramework.getFrameworkVars()
  const hubStateProps = ({ auth }: StoreState) => ({ vars, auth })
  const commonModuleStateProps = ({ auth, commonModule, theme }: SparkyStoreState) => ({
    vars,
    fxVars,
    auth,
    commonModule,
    theme,
  })
  const isNetSec = sparkyFramework.isOidcClient(fxVars.netsec_client_id || 'scm')

  const getCommonServicePages = (): NavItem[] => {
    if (!fxVars.use_new_left_nav || fxVars.hub) {
      return []
    }

    const pages: NavItem[] = [
      {
        key: 'subscriptions',
        path: '/settings/subscriptions',
        title: 'Subscriptions',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: Subscriptions,
        mapStateToProps: commonModuleStateProps,
        ...commonServicesAvail,
      },
      {
        key: 'tenants',
        path: '/settings/tenants',
        title: 'Tenants',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: TenantManagement,
        mapStateToProps: commonModuleStateProps,
        avail: ({ access, auth }: any) => {
          if (vars.default_tsg) {
            const { tsgs } = auth
            return tsgs?.hasMsp && commonServicesAvail.avail({ access })
          }
          return true
        },
      },
      (vars.default_tsg && {
        key: 'products',
        path: '/settings/products',
        title: 'Products',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: TenantManagement,
        mapStateToProps: commonModuleStateProps,
        avail: ({ access, auth }: any) => {
          const { tsgs } = auth
          return !tsgs?.hasMsp && commonServicesAvail.avail({ access })
        },
      }),
      (vars.das_enabled && {
        key: 'das',
        path: '/settings/devices',
        title: 'Device Associations',
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: DevicesAssociations,
        mapStateToProps: commonModuleStateProps,
        ...commonServicesAvail,
      }),
      {
        key: 'iam',
        path: '/settings/iam',
        title: 'Identity & Access',
        expandable: false,
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: IAMTabs,
        mapStateToProps: commonModuleStateProps,
        ...commonServicesAvail,
      },
      (vars.ip_allow_list ? (
        {
          key: 'ip_restrictions',
          path: '/settings/ip',
          title: 'Trusted IP List',
          contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
          component: IpAllowList,
          mapStateToProps: commonModuleStateProps,
          ...commonServicesAvail,
        }) : false
      ),
      sparkyFramework.getFrameworkVar('user_preferences_ui') && {
        key: 'preference',
        path: '/settings/preferences',
        title: 'User Preferences',
        expandable: false,
        contentClassName: 'panwds-tw3 panwds-unset-reboot cm-h-full',
        component: UserPreferences,
        mapStateToProps: ({ theme, preferences }: StoreState) => ({ theme: preferences?.user?.theme || theme }),
        ...commonServicesAvail,
      },
      vars.dev && {
        key: 'dev',
        path: '/dev',
        hidden: true,
        component: Dev,
        mapStateToProps: () => ({}),
      }
    ].filter(Boolean)

    const settings: NavItem[] = [{
      key: 'settings',
      path: '/settings',
      title: 'Settings',
      icon: SettingsIcon,
      expandable: true,
      mergeNav: true,
      children: pages,
    }]

    if (sparkyFramework.hasMicroApp(MSP_MICRO_APP)) {
      settings.push({
        key: 'msp-settings',
        mergeNav: true,
        title: 'Settings',
        icon: SettingsIcon,
        hidden: true,
        children: pages.map(item => ({ ...item, key: `msp-${item.key}`, path: `/msp${item.path}` }))
      } as NavItem)
    }

    return settings
  }

  return ({
    navigation: [
      ...getCommonServicePages(),
      ...(!isNetSec ? [{
        key: 'subscription',
        title: 'Subscription',
        path: '/setup',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        component: SubscriptionFlowCheck,
        hidden: true,
      }] : []),
      {
        key: 'profile',
        title: 'Profile',
        path: '/activation/profile',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        component: Activation,
        hidden: true,
      },
      ...(vars.unified_activation ? [{
        key: 'activation',
        title: 'Activation',
        path: '/activation',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3 panwds-unset-reboot',
        mapStateToProps: commonModuleStateProps,
        component: Activation,
        hidden: true,
      }] : []),
      ...(fxVars.hub ? [{ // alias to /setup for hub2
        key: 'hub-subscription',
        title: 'Hub Subscription',
        path: '/hub/setup',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        component: SubscriptionFlowCheck,
        hidden: true,
      }, {
        key: 'hub',
        title: 'Hub',
        path: '/hub',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3',
        mapStateToProps: hubStateProps,
        component: Hub,
        hidden: true,
      }, {
        key: 'products',
        path: '/marketplace',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3',
        mapStateToProps: hubStateProps,
        component: Hub,
        hidden: true,
      }] : []),
      ...([{
        key: 'ztpdeviceactivation',
        title: 'ZTP Device Activation',
        path: '/ztpdeviceactivation',
        noRBACheck: true,
        bypassTsgAvail: true,
        bypassGlobalAvail: true,
        contentClassName: 'panwds-tw3',
        mapStateToProps: commonModuleStateProps,
        component: ZTPDeviceActivation,
        hidden: true,
      }])
    ].filter(Boolean) as NavItem[],
    actions: [
      {
        key: 'tenant-switcher',
        bottomMost: true,
        bypassTsgAvail: true,
        title: () => sparkyFramework.getAuthState()?.tsg_name || null,
        icon: TsgNavIcon,
        mapStateToProps: fxVars.support_msp_tenant_switcher ? (state: SparkyStoreState) => ({
          ...commonModuleStateProps(state), // TODO: update the temp regex once MSP support the new tenant selector in all msp pages
          pathPrefix: window.location.pathname.replace(/^\/(msp\/manage\/(?:snippets)|msp|asc)?\b.*$/, '/$1'),
        }) : commonModuleStateProps,
        navComponent: fxVars.use_new_left_nav && !fxVars.hub ?
          TenantAppSwitcher : TenantSwitcher
      }
    ],
  })
}

export default microApp
